import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Holiday = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [formData, setFormData] = useState({
    date: '', // Store in YYYY-MM-DD format for date input
    day: '',
    holiday: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.limit, search, year, sortBy, sortOrder]);

  const fetchHolidays = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'list-holiday',
        currentUserId: currentUserId,
        year: year,
        ...(search && { search: search }),
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(sortBy && { sortBy: sortBy, sortOrder: sortOrder })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setHolidays(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch holidays');
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Handle format like "26-01-2026"
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = parseInt(month) - 1;
          return `${day}-${monthNames[monthIndex]}-${year}`;
        }
      }
      // Handle ISO date format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditingHolidayId(null);
    setShowAddModal(true);
    setFormData({
      date: '', // YYYY-MM-DD format
      day: '',
      holiday: ''
    });
    setFormErrors({});
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEditMode(false);
    setEditingHolidayId(null);
    setFormData({
      date: '', // YYYY-MM-DD format
      day: '',
      holiday: ''
    });
    setFormErrors({});
  };

  // Convert date from DD-MM-YYYY to YYYY-MM-DD for date input
  const convertDateToInputFormat = (dateString) => {
    if (!dateString) return '';
    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // parts[0] = day, parts[1] = month, parts[2] = year
        return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value; // Format: YYYY-MM-DD from date input
    
    // Validate year is 4 digits
    if (selectedDate) {
      const parts = selectedDate.split('-');
      if (parts.length === 3 && parts[0].length !== 4) {
        // Year must be 4 digits
        setFormErrors(prev => ({ ...prev, date: 'Year must be 4 digits' }));
        return;
      }
    }
    
    let day = '';
    
    // Only calculate day if we have a complete valid date
    if (selectedDate && selectedDate.length === 10) {
      const date = new Date(selectedDate);
      if (!isNaN(date.getTime())) {
        // Calculate day name
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        day = days[date.getDay()];
      }
    }

    setFormData(prev => ({
      ...prev,
      date: selectedDate, // Store in YYYY-MM-DD format
      day: day
    }));

    // Clear date error if exists
    if (formErrors.date) {
      setFormErrors(prev => ({ ...prev, date: '' }));
    }
  };

  const handleHolidayChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      holiday: value
    }));

    // Clear holiday error if exists
    if (formErrors.holiday) {
      setFormErrors(prev => ({ ...prev, holiday: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.date) {
      errors.date = 'Date is required';
    } else {
      // Validate date format and year
      const parts = formData.date.split('-');
      if (parts.length !== 3 || parts[0].length !== 4) {
        errors.date = 'Please enter a valid date with 4-digit year';
      } else {
        const date = new Date(formData.date);
        if (isNaN(date.getTime())) {
          errors.date = 'Please enter a valid date';
        }
      }
    }

    if (!formData.holiday || !formData.holiday.trim()) {
      errors.holiday = 'Holiday name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      // Convert date from YYYY-MM-DD to DD-MM-YYYY for API
      let formattedDate = '';
      if (formData.date) {
        const parts = formData.date.split('-');
        if (parts.length === 3) {
          formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
        }
      }

      let payload;
      if (isEditMode && editingHolidayId) {
        // Update holiday
        payload = {
          action: 'update-holiday',
          id: editingHolidayId,
          date: formattedDate,
          day: formData.day,
          holiday: formData.holiday.trim(),
          createdBy: currentUserId,
          currentUserId: currentUserId
        };
      } else {
        // Add holiday
        payload = {
          action: 'add-holiday',
          date: formattedDate,
          day: formData.day,
          holiday: formData.holiday.trim(),
          createdBy: currentUserId
        };
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || (isEditMode ? 'Holiday updated successfully' : 'Holiday added successfully'));
        closeAddModal();
        fetchHolidays();
      } else {
        toast.error(result?.message || (isEditMode ? 'Failed to update holiday' : 'Failed to add holiday'));
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} holiday:`, error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    if (!holiday?._id) {
      toast.error('Unable to open holiday. Missing identifier.');
      return;
    }

    // Convert date from DD-MM-YYYY to YYYY-MM-DD for date input
    const dateInInputFormat = convertDateToInputFormat(holiday.date);

    setIsEditMode(true);
    setEditingHolidayId(holiday._id);
    setShowAddModal(true);
    setFormData({
      date: dateInInputFormat, // YYYY-MM-DD format
      day: holiday.day || '',
      holiday: holiday.holiday || ''
    });
    setFormErrors({});
  };

  const handleDelete = (holiday) => {
    setHolidayToDelete(holiday);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setHolidayToDelete(null);
  };

  const confirmDelete = async () => {
    if (!holidayToDelete) return;

    const updatedBy = localStorage.getItem('id');

    if (!updatedBy) {
      toast.error('User ID not found. Please login again.');
      closeDeleteModal();
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      const payload = {
        action: 'delete-holiday',
        id: holidayToDelete._id,
        updatedBy: updatedBy,
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Holiday deleted successfully');
        closeDeleteModal();
        fetchHolidays();
      } else {
        toast.error(result?.message || 'Failed to delete holiday');
      }
    } catch (error) {
      console.error('Error deleting holiday:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Add Button */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Manage Holidays
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Manage holidays for your organization by adding, updating, or deleting holiday entries.
              </p>
            </div>
             <div className="col-md-4 employee-view-header-actions">
               <button type="button" className="employee-back-btn" onClick={handleAdd}>
                 Add Holiday
               </button>
             </div>
          </div>

          {/* Holiday Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px' }}>
                    <input  
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search holidays..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                    <label htmlFor="year" className="form-label" style={{ marginBottom: '5px', fontSize: '12px', color: '#6b7280', display: 'block' }}>
                      Year
                    </label>
                    <div className="date-input-wrapper" style={{ position: 'relative' }}>
                      <input
                        type="number"
                        id="year"
                        className="form-input"
                        placeholder="Select Year"
                        value={year}
                        onChange={(e) => {
                          const yearValue = parseInt(e.target.value, 10);
                          if (!isNaN(yearValue) && yearValue >= 2000 && yearValue <= 2100) {
                            setYear(yearValue);
                            setPagination(prev => ({ ...prev, currentPage: 1 }));
                          } else if (e.target.value === '') {
                            setYear(new Date().getFullYear());
                          }
                        }}
                        min="2000"
                        max="2100"
                        style={{ 
                          padding: '8px 35px 8px 12px', 
                          fontSize: '14px',
                          height: '40px',
                          width: '150px'
                        }}
                      />
                      <i className="fas fa-calendar-alt date-icon" style={{ 
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        pointerEvents: 'none',
                        fontSize: '14px'
                      }}></i>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th className="employee-sortable">S.No</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('holiday')}
                            >
                              Holidays
                              {sortBy === 'holiday' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'holiday' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('date')}
                            >
                              Date
                              {sortBy === 'date' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'date' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('day')}
                            >
                              Day
                              {sortBy === 'day' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'day' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {holidays.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="employee-empty">
                                No holidays found
                              </td>
                            </tr>
                          ) : (
                            holidays.map((holiday, index) => {
                              return (
                                <tr key={holiday._id}>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{holiday.holiday || '-'}</td>
                                  <td>{formatDate(holiday.date)}</td>
                                  <td>{holiday.day || '-'}</td>
                                  <td>
                                    <div className="employee-actions">
                                      <button
                                        className="employee-action-btn employee-action-edit"
                                        onClick={() => handleEdit(holiday)}
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pencil"></i>
                                      </button>
                                      <button
                                        className="employee-action-btn employee-action-delete"
                                        onClick={() => handleDelete(holiday)}
                                        title="Delete"
                                      >
                                        <i className="fa-solid fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {holidays.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                          <label htmlFor="holidayPaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                            Show:
                          </label>
                          <select
                            id="holidayPaginationLimit"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{
                              padding: '6px 10px',
                              fontSize: '14px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              minWidth: '70px'
                            }}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="75">75</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

       {/* Add/Edit Holiday Modal */}
       {showAddModal && (
         <div className="modal-overlay" onClick={closeAddModal}>
           <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
             <div className="modal-header">
               <h3>{isEditMode ? 'Edit Holiday' : 'Add Holiday'}</h3>
               <button className="modal-close" onClick={closeAddModal}>
                 <i className="fas fa-times"></i>
               </button>
             </div>
             <form onSubmit={handleSubmit}>
               <div className="modal-body">
                 <div className="form-group">
                   <label htmlFor="date" className="form-label">
                     Date <span className="text-danger">*</span>
                   </label>
                   <input
                     type="date"
                     id="date"
                     className={`form-input ${formErrors.date ? 'error' : ''}`}
                     value={formData.date}
                     onChange={handleDateChange}
                     required
                     min="1900-01-01"
                     max="2100-12-31"
                   />
                   {formErrors.date && (
                     <span className="error-message">{formErrors.date}</span>
                   )}
                 </div>

                 <div className="form-group">
                   <label htmlFor="day" className="form-label">
                     Day
                   </label>
                   <input
                     type="text"
                     id="day"
                     className="form-input"
                     value={formData.day}
                     readOnly
                   />
                 </div>

                 <div className="form-group">
                   <label htmlFor="holiday" className="form-label">
                     Holiday <span className="text-danger">*</span>
                   </label>
                   <input
                     type="text"
                     id="holiday"
                     className={`form-input ${formErrors.holiday ? 'error' : ''}`}
                     placeholder="Enter holiday name"
                     value={formData.holiday}
                     onChange={handleHolidayChange}
                     required
                   />
                   {formErrors.holiday && (
                     <span className="error-message">{formErrors.holiday}</span>
                   )}
                 </div>
               </div>
               <div className="modal-footer">
                 <button
                   type="button"
                   className="btn btn-secondary"
                   onClick={closeAddModal}
                 >
                   Cancel
                 </button>
                 <button
                   type="submit"
                   className="btn btn-primary"
                   disabled={loading}
                 >
                   {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Holiday' : 'Add Holiday')}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}

       {/* Delete Confirmation Modal */}
       {showDeleteModal && (
         <div className="modal-overlay" onClick={closeDeleteModal}>
           <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
             <div className="modal-header">
               <h3>Confirm Delete</h3>
               <button className="modal-close" onClick={closeDeleteModal}>
                 <i className="fas fa-times"></i>
               </button>
             </div>
             <div className="modal-body">
               <p>Are you sure you want to delete <strong>{holidayToDelete?.holiday} Holiday</strong>?</p>
             </div>
             <div className="modal-footer">
               <button className="btn-primary" onClick={confirmDelete}>
                 Yes
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };
 
 export default Holiday;
